import { useLocation } from "@remix-run/react";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from "react";

import { createTrack, identify as clientIdentify, reset as clientReset } from "./client";
import type { AnalyticsInstance, EventMapFromSchemas, EventSchemaMap, PrefixedEventSchemaMap, TrackFn } from "./types";

const noopTrack = () => {};
const noopIdentify = () => {};
const noopReset = () => {};

export function createAnalytics<TSchemas extends EventSchemaMap>(schemas: TSchemas) {
  type TEvents = EventMapFromSchemas<TSchemas>;

  const clientTrack = createTrack<TEvents>();

  // In dev: validate properties against the schema before sending to Mixpanel
  const validatedTrack: TrackFn<TEvents> = (event, properties) => {
    if (process.env.NODE_ENV === "development") {
      const schema = schemas[event as string];
      if (schema) {
        const result = schema.safeParse(properties);
        if (!result.success) {
          console.warn(`[analytics] Invalid properties for event "${event}":`, result.error.format());
        }
      }
    }
    clientTrack(event, properties);
  };

  // ── Context ────────────────────────────────────────────────────────────────
  const AnalyticsContext = createContext<AnalyticsInstance<TEvents>>({
    track: noopTrack as TrackFn<TEvents>,
    identify: noopIdentify,
    reset: noopReset,
  });

  // ── Provider ───────────────────────────────────────────────────────────────
  function AnalyticsProvider({ children, enabled = false }: { children: ReactNode; enabled?: boolean }) {
    const value = useMemo<AnalyticsInstance<TEvents>>(
      () =>
        enabled
          ? {
              track: validatedTrack,
              identify: clientIdentify,
              reset: clientReset,
            }
          : {
              track: noopTrack as TrackFn<TEvents>,
              identify: noopIdentify,
              reset: noopReset,
            },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [enabled],
    );
    return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
  }

  // ── Hook ───────────────────────────────────────────────────────────────────
  function useAnalytics(): AnalyticsInstance<TEvents> {
    return useContext(AnalyticsContext);
  }

  // ── useTrackOnce ───────────────────────────────────────────────────────────
  // Fires the event exactly once per mount of the calling component.
  // Must be called unconditionally (Rules of Hooks); the guard is internal.
  function useTrackOnce<E extends keyof TEvents & string>(event: E, properties: TEvents[E]): void {
    const { track } = useContext(AnalyticsContext);
    const firedRef = useRef(false);

    useEffect(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      track(event, properties);
      // properties is intentionally omitted — snapshot at mount time is correct
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [track, event]);
  }

  // ── Page view tracker ──────────────────────────────────────────────────────
  function PageViewTracker({ pageViewEventKey = "page_view" }: { pageViewEventKey?: string }) {
    const location = useLocation();
    const { track } = useAnalytics();
    const previousPath = useRef<string | null>(null);

    useEffect(() => {
      const current = location.pathname + location.search;
      if (previousPath.current === current) return;
      previousPath.current = current;
      (track as TrackFn<Record<string, Record<string, unknown>>>)(pageViewEventKey, {
        path: location.pathname,
        search: location.search || undefined,
        referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
      });
    }, [location.pathname, location.search, track, pageViewEventKey]);

    return null;
  }

  return { AnalyticsProvider, useAnalytics, useTrackOnce, PageViewTracker };
}

export type { EventSchemaMap };

/**
 * defineEvents — constrains a schema record to EventSchemaMap and optionally
 * prefixes all keys with "namespace." to produce dotted Mixpanel event names.
 *
 * Usage (namespaced):
 *   export const events = defineEvents("onboarding-wizard", {
 *     started: z.object({}),
 *     step_completed: z.object({ from_step: z.string(), to_step: z.string() }),
 *   });
 *   // keys: "onboarding-wizard.started", "onboarding-wizard.step_completed"
 *
 * Usage (no namespace — legacy):
 *   export const events = defineEvents({ my_event: z.object({}) });
 */
export function defineEvents<TNamespace extends string, T extends EventSchemaMap>(
  namespace: TNamespace,
  events: T,
): PrefixedEventSchemaMap<TNamespace, T>;
export function defineEvents<T extends EventSchemaMap>(events: T): T;
export function defineEvents<TNamespace extends string, T extends EventSchemaMap>(
  namespaceOrEvents: TNamespace | T,
  events?: T,
): PrefixedEventSchemaMap<TNamespace, T> | T {
  if (typeof namespaceOrEvents === "string") {
    const namespace = namespaceOrEvents;
    const raw = events as T;
    const prefixed: EventSchemaMap = {};
    for (const key of Object.keys(raw)) {
      prefixed[`${namespace}.${key}`] = raw[key]!;
    }
    return prefixed as PrefixedEventSchemaMap<TNamespace, T>;
  }
  return namespaceOrEvents;
}
